/**
 * Firebase & Google Sign-In Diagnostic Utility
 *
 * Run this in the browser console to check Firebase configuration
 */

import { auth } from '../firebase/firebase-config';

export interface DiagnosticResult {
  status: 'pass' | 'fail' | 'warning';
  message: string;
  details?: Record<string, unknown>;
}

export interface DiagnosticReport {
  timestamp: string;
  overallStatus: 'healthy' | 'issues' | 'critical';
  checks: {
    firebaseInit: DiagnosticResult;
    authInstance: DiagnosticResult;
    domainCheck: DiagnosticResult;
    popupTest: DiagnosticResult;
  };
  recommendations: string[];
}

/**
 * Run comprehensive diagnostics on Firebase/Google Sign-In setup
 */
export async function runFirebaseDiagnostics(): Promise<DiagnosticReport> {
  const checks: DiagnosticReport['checks'] = {
    firebaseInit: { status: 'fail', message: 'Not checked' },
    authInstance: { status: 'fail', message: 'Not checked' },
    domainCheck: { status: 'fail', message: 'Not checked' },
    popupTest: { status: 'fail', message: 'Not checked' },
  };

  const recommendations: string[] = [];

  // Check 1: Firebase Initialization
  try {
    if (auth) {
      checks.firebaseInit = {
        status: 'pass',
        message: 'Firebase initialized successfully',
        details: {
          appName: auth.app.name,
          apiKey: auth.app.options.apiKey?.substring(0, 10) + '...',
          authDomain: auth.app.options.authDomain,
        },
      };
    } else {
      checks.firebaseInit = {
        status: 'fail',
        message: 'Firebase auth is not initialized',
      };
      recommendations.push(
        'Check firebase-config.ts file and ensure Firebase is properly initialized'
      );
    }
  } catch (error: unknown) {
    checks.firebaseInit = {
      status: 'fail',
      message: 'Error checking Firebase initialization',
      details: { error: error instanceof Error ? error.message : String(error) },
    };
  }

  // Check 2: Auth Instance
  try {
    if (auth && auth.currentUser !== undefined) {
      checks.authInstance = {
        status: 'pass',
        message: 'Auth instance is working',
        details: {
          currentUser: auth.currentUser ? auth.currentUser.email : 'Not logged in',
        },
      };
    } else if (!auth) {
      checks.authInstance = {
        status: 'fail',
        message: 'Auth instance not available',
      };
      recommendations.push('Firebase auth object is undefined');
    }
  } catch (error: unknown) {
    checks.authInstance = {
      status: 'fail',
      message: 'Error checking auth instance',
      details: { error: error instanceof Error ? error.message : String(error) },
    };
  }

  // Check 3: Domain Configuration
  try {
    const currentDomain = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;
    const fullDomain = `${protocol}//${currentDomain}${port ? ':' + port : ''}`;

    const authorizedDomains = [
      'localhost',
      'clinicaltrialexpertise.co.za',
      'studio-9483986446-a3660.firebaseapp.com',
    ];

    const isAuthorized = authorizedDomains.some(
      (domain) => currentDomain === domain || currentDomain.includes(domain)
    );

    if (isAuthorized) {
      checks.domainCheck = {
        status: 'pass',
        message: 'Domain is authorized',
        details: {
          currentDomain: fullDomain,
          authorizedDomains,
        },
      };
    } else {
      checks.domainCheck = {
        status: 'warning',
        message: 'Domain may not be authorized in Firebase',
        details: {
          currentDomain: fullDomain,
          expectedDomains: authorizedDomains,
        },
      };
      recommendations.push(
        `Add "${currentDomain}" to Firebase Console → Authentication → Settings → Authorized domains`
      );
    }
  } catch (error: unknown) {
    checks.domainCheck = {
      status: 'fail',
      message: 'Error checking domain',
      details: { error: error instanceof Error ? error.message : String(error) },
    };
  }

  // Check 4: Pop-up Blocker Test
  try {
    // Try to open and immediately close a test popup
    const testPopup = window.open('', '_blank', 'width=1,height=1');
    if (testPopup) {
      testPopup.close();
      checks.popupTest = {
        status: 'pass',
        message: 'Pop-ups are allowed',
      };
    } else {
      checks.popupTest = {
        status: 'fail',
        message: 'Pop-ups are blocked',
      };
      recommendations.push('Enable pop-ups for this site in your browser settings');
    }
  } catch (error: unknown) {
    checks.popupTest = {
      status: 'fail',
      message: 'Pop-up blocker is active',
      details: { error: error instanceof Error ? error.message : String(error) },
    };
    recommendations.push('Enable pop-ups for this site in your browser settings');
  }

  // Determine overall status
  const failCount = Object.values(checks).filter((c) => c.status === 'fail').length;
  const warningCount = Object.values(checks).filter((c) => c.status === 'warning').length;

  let overallStatus: 'healthy' | 'issues' | 'critical';
  if (failCount === 0 && warningCount === 0) {
    overallStatus = 'healthy';
  } else if (failCount > 0) {
    overallStatus = 'critical';
  } else {
    overallStatus = 'issues';
  }

  // Add general recommendations if there are critical issues
  if (overallStatus === 'critical') {
    recommendations.unshift(
      'CRITICAL: Firebase Google Sign-In is not properly configured',
      'Follow the steps in GOOGLE-SIGNIN-FIX.md to resolve these issues'
    );
  }

  return {
    timestamp: new Date().toISOString(),
    overallStatus,
    checks,
    recommendations,
  };
}

/**
 * Print diagnostic report to console in a readable format
 */
export function printDiagnosticReport(report: DiagnosticReport): void {
  console.group('🔍 Firebase & Google Sign-In Diagnostics');
  console.log('⏰ Timestamp:', report.timestamp);
  console.log('📊 Overall Status:', report.overallStatus.toUpperCase());
  console.log('');

  console.group('✅ Diagnostic Checks');
  Object.entries(report.checks).forEach(([key, result]) => {
    const icon = result.status === 'pass' ? '✅' : result.status === 'warning' ? '⚠️' : '❌';
    console.group(`${icon} ${key}`);
    console.log('Status:', result.status);
    console.log('Message:', result.message);
    if (result.details) {
      console.log('Details:', result.details);
    }
    console.groupEnd();
  });
  console.groupEnd();

  if (report.recommendations.length > 0) {
    console.group('💡 Recommendations');
    report.recommendations.forEach((rec, index) => {
      console.log(`${index + 1}. ${rec}`);
    });
    console.groupEnd();
  }

  console.groupEnd();
}

/**
 * Run diagnostics and print to console
 * Usage in browser console: runDiagnostics()
 */
export async function runDiagnostics(): Promise<void> {
  console.log('🔄 Running Firebase diagnostics...');
  const report = await runFirebaseDiagnostics();
  printDiagnosticReport(report);

  if (report.overallStatus === 'healthy') {
    console.log('✅ All checks passed! Google Sign-In should work correctly.');
  } else if (report.overallStatus === 'issues') {
    console.log('⚠️ Some warnings detected. Google Sign-In may still work.');
  } else {
    console.log('❌ Critical issues detected. Google Sign-In will NOT work until resolved.');
    console.log('📖 See GOOGLE-SIGNIN-FIX.md for detailed instructions');
  }
}

// Make available globally for browser console
declare global {
  interface Window {
    runFirebaseDiagnostics?: () => Promise<void>;
  }
}

if (typeof window !== 'undefined') {
  window.runFirebaseDiagnostics = runDiagnostics;
  console.log(
    '💡 Diagnostic tool loaded! Run "runFirebaseDiagnostics()" in console to check configuration'
  );
}
